import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts';

export const AvgRevenuePerCustomerChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const avgRevenue = useMemo(() => {
        const grossSales = finance?.metrics?.grossSales ?? 0;
        const customers = finance?.metrics?.customers ?? 0;
        if (customers === 0) return 0;
        return grossSales / customers;
    }, [finance?.metrics?.grossSales, finance?.metrics?.customers]);

    const sparkData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const grossSales = metrics?.grossSales ?? 0;
                const customers = metrics?.customers ?? 0;
                const avg = customers > 0 ? grossSales / customers : 0;
                return { date, avg };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => !finance || avgRevenue === 0, [finance, avgRevenue]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="px-4 pt-3 flex items-center justify-center [container-type:inline-size]">
                <p
                    className="font-semibold text-center"
                    style={{
                        color: CHART_COLORS.primary,
                        fontSize: 'clamp(1.25rem, 8cqi, 2.5rem)',
                    }}
                >
                    {formatCompactNumber(avgRevenue, currencySymbol)}
                </p>
            </div>
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={sparkData}
                        margin={{ top: 5, right: 10, bottom: 5, left: 10 }}
                    >
                        <XAxis dataKey="date" hide />
                        <YAxis hide />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.avgRevenuePerCustomer', 'Avg Revenue / Customer'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Line
                            type="monotone"
                            dataKey="avg"
                            stroke={CHART_COLORS.primary}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.primary }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
